import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import { CalculateCartPromotion } from "../../modules/basket/types/CalculateCart";
import { IModifierItem, IProductInfo } from "../../modules/home/types/Menu";
import {
  clampToCartLimits,
  isCartLimitReached,
} from "../../modules/basket/libs/cartLimit";

export type BasketItem = IProductInfo & {
  quantity: number;
  isAvailable: boolean;
  cartKey: string;
  total_price: number;
  selectedModifiers?: BasketModifierSelection[];
};

export type BasketModifierSelection = {
  groupId: string;
  groupSku?: string;
  groupName: string;
  itemId: string;
  itemSku?: string;
  itemName: string;
  quantity: number;
  price: number;
  item?: IModifierItem;
};

export type PromoValidationStatus = "none" | "valid" | "invalid";

// Matches basket items against a branch's menu (by id+title, falling back to
// title alone) and marks each as available/unavailable there. Shared by the
// updateBasketWithNewMenu reducer and any code checking availability without
// dispatching (e.g. the pickup branch-confirmation flow).
export const matchBasketItemsWithMenu = (
  items: BasketItem[],
  newMenu: IProductInfo[],
): BasketItem[] =>
  items.map((item) => {
    const exactMatch = newMenu.find(
      (menuItem) => menuItem.id === item.id && menuItem.title === item.title,
    );

    if (exactMatch) {
      return {
        ...item,
        // SKU подтягиваем из свежего меню: позиции, сохранённые до того, как
        // адаптер меню начал его передавать, лежат в корзине без него.
        sku: exactMatch.sku ?? item.sku,
        // Лимит тоже из меню: его могли поменять, а у старых позиций его нет.
        max_cart_qty: exactMatch.max_cart_qty ?? null,
        isAvailable: true,
      };
    }

    const titleMatch = newMenu.find(
      (menuItem) => menuItem.title === item.title,
    );

    if (titleMatch) {
      return {
        ...titleMatch,
        quantity: item.quantity,
        isAvailable: true,
        cartKey: item.cartKey,
        total_price: item.total_price,
        selectedModifiers: item.selectedModifiers,
      };
    }

    return {
      ...item,
      isAvailable: false,
    };
  });

interface BasketState {
  items: BasketItem[];
  // Идёт сверка корзины с меню филиала (см. useGetNearesBranch). Пока true,
  // `isAvailable: false` у позиции — ещё не приговор, а прошлое состояние из
  // хранилища: корзина показывает шиммер, а не «нет в наличии».
  isAvailabilityPending: boolean;
  isCalculatingCart: boolean;
  calculatedSubtotalPrice: number | null;
  calculatedDeliveryPrice: number | null;
  calculatedTotalPrice: number | null;
  appliedPromotions: CalculateCartPromotion[];
  earnedAmount: number;
  availableAmountForCurrentOrder: number;
  availablePoints: number;
  /**
   * Тумблер «Списать баллы». Сумму пользователь не выбирает: пока тумблер
   * включён, списывается весь discountAmountForCurrentOrder из последнего
   * calculate-cart (selectedBonusPay ниже). В сторе, а не в экране — чтобы
   * выбор из корзины доехал до чекаута.
   */
  pointsEnabled: boolean;
  /** Лимит списания в % от чека из calculate-cart; null — бэкенд не прислал. */
  bonusPaymentPercent: number | null;
  selectedBonusPay: number;
  promoValidationStatus: PromoValidationStatus;
  couponStatus: string | null;
  /**
   * Применённый промокод — выбор пользователя (с экрана «Промокоды» или
   * «Применить» на чекауте). Живёт, пока не случится событие сброса:
   * закрытие мини-аппа (стор только в памяти), выход из аккаунта, корзина
   * стала пустой, применён другой код, заказ оформлен, ручная отмена.
   *
   * Отказ бэкенда код НЕ снимает: он уходит в каждый пересчёт и проверяется
   * заново на текущем составе корзины — убрал мешающий товар, и код снова
   * действует. Результат проверки — promoValidationStatus/activePromoCode.
   */
  promoCode: string;
  /** promoCode, который бэкенд принял в последнем расчёте; иначе "". */
  activePromoCode: string;
}

const initialState: BasketState = {
  isAvailabilityPending: false,
  items: [],
  isCalculatingCart: false,
  calculatedSubtotalPrice: null,
  calculatedDeliveryPrice: null,
  calculatedTotalPrice: null,
  appliedPromotions: [],
  earnedAmount: 0,
  availableAmountForCurrentOrder: 0,
  availablePoints: 0,
  pointsEnabled: false,
  bonusPaymentPercent: null,
  selectedBonusPay: 0,
  promoValidationStatus: "none",
  couponStatus: null,
  promoCode: "",
  activePromoCode: "",
};

/** Всё про промокод — в исходное. Один набор для каждого события сброса. */
const resetPromoState = (state: BasketState) => {
  state.promoCode = "";
  state.activePromoCode = "";
  state.appliedPromotions = [];
  state.promoValidationStatus = "none";
  state.couponStatus = null;
};

/**
 * Корзина стала пустой (удалён последний товар) — промокод сбрасывается.
 * Только по переходу «были товары → нет»: промокод, выбранный при пустой
 * корзине, служебные обновления не снимают.
 */
const dropPromoIfEmptied = (state: BasketState, hadItems: boolean) => {
  if (hadItems && !state.items.length) resetPromoState(state);
};

const basketSlice = createSlice({
  name: "basket",
  initialState,
  reducers: {
    setBasketFromServer(state, action: PayloadAction<BasketItem[]>) {
      const hadItems = state.items.length > 0;
      state.items = action.payload.map((item) => ({
        ...item,
      }));
      dropPromoIfEmptied(state, hadItems);
    },
    /**
     * Перечитать корзину из хранилища при открытии экрана. Состав и
     * количества берём оттуда, а вот доступность у уже известных позиций
     * оставляем нашу: сверка с меню доставки (updateBasketWithNewMenu) в
     * хранилище не пишет, а сверка для самовывоза пишет — и после неё в
     * хранилище лежит «нет в наличии» для чужого филиала. Без этого при
     * каждом заходе в корзину товар мигал недоступным, пока не приедет
     * свежая сверка.
     */
    hydrateBasketFromStorage(state, action: PayloadAction<BasketItem[]>) {
      const known = new Map(state.items.map((item) => [item.cartKey, item]));
      const hadItems = state.items.length > 0;

      state.items = clampToCartLimits(
        action.payload.map((item) => {
          const current = known.get(item.cartKey);

          return {
            ...item,
            isAvailable: current?.isAvailable ?? item.isAvailable,
            // Лимит тоже наш: в хранилище позиция могла лечь ещё без него, а
            // сверка с меню его уже подтянула.
            max_cart_qty: current ? current.max_cart_qty : item.max_cart_qty,
          };
        }),
      );
      dropPromoIfEmptied(state, hadItems);
    },
    setAvailabilityPending(state, action: PayloadAction<boolean>) {
      state.isAvailabilityPending = action.payload;
    },
    addToBasket(state, action: PayloadAction<BasketItem>) {
      // Сервис useAddToBasket проверяет лимит сам и показывает тост — здесь
      // страховка для любого другого места, что диспатчит напрямую.
      if (isCartLimitReached(state.items, action.payload)) return;
      const index = state.items.findIndex(
        (item) => item.cartKey === action.payload.cartKey,
      );
      if (index > -1) {
        state.items[index].quantity += 1;
      } else {
        state.items.push(action.payload);
      }
    },
    removeFromBasket(state, action: PayloadAction<string>) {
      const hadItems = state.items.length > 0;
      state.items = state.items.filter(
        (item) => item.cartKey !== action.payload,
      );
      dropPromoIfEmptied(state, hadItems);
    },
    increaseQuantity(state, action: PayloadAction<string>) {
      const item = state.items.find((i) => i.cartKey === action.payload);
      if (item && !isCartLimitReached(state.items, item)) item.quantity += 1;
    },
    decreaseQuantity(state, action: PayloadAction<string>) {
      const item = state.items.find((i) => i.cartKey === action.payload);
      if (item) {
        item.quantity -= 1;
        if (item.quantity <= 0) {
          state.items = state.items.filter((i) => i.cartKey !== action.payload);
          dropPromoIfEmptied(state, true);
        }
      }
    },
    removeNotAvailableItems(state, action: PayloadAction<string>) {
      const item = state.items.find((i) => i.cartKey === action.payload);

      if (item && !item.isAvailable) {
        state.items = state.items.filter((i) => i.cartKey !== action.payload);
        dropPromoIfEmptied(state, true);
      }
    },
    removeAllNotAvailableItems(state) {
      const hadItems = state.items.length > 0;
      state.items = state.items.filter((item) => item.isAvailable);
      dropPromoIfEmptied(state, hadItems);
    },

    updateBasketWithNewMenu(state, action: PayloadAction<IProductInfo[]>) {
      state.isAvailabilityPending = false;

      // An empty menu means the request gave us nothing usable — not that the
      // branch stocks nothing. Marking every item unavailable here zeroes the
      // basket, so leave availability untouched until a real menu arrives.
      if (!action.payload?.length) return;

      const next = matchBasketItemsWithMenu(state.items, action.payload);

      // Likewise, if *every* item came back unavailable we're almost certainly
      // diffing against the wrong branch's menu (or a response we failed to
      // parse) rather than looking at a branch that genuinely stocks none of
      // it. Emptying the whole basket on that guess is never the right call —
      // a real partial mismatch still applies normally.
      if (state.items.length && next.every(item => !item.isAvailable)) return;

      // Меню принесло свежие max_cart_qty — лишнее сверх лимита срезаем.
      state.items = clampToCartLimits(next);
    },

    clearBasket(state) {
      state.items = [];
      state.isCalculatingCart = false;
      state.calculatedSubtotalPrice = 0;
      state.calculatedDeliveryPrice = 0;
      state.calculatedTotalPrice = 0;
      state.appliedPromotions = [];
      state.earnedAmount = 0;
      state.availableAmountForCurrentOrder = 0;
      state.availablePoints = 0;
      state.pointsEnabled = false;
      state.selectedBonusPay = 0;
      // Очистка корзины и успешный заказ (он чистит корзину) — сброс кода.
      resetPromoState(state);
    },
    setCartCalculating(state, action: PayloadAction<boolean>) {
      state.isCalculatingCart = action.payload;
    },
    setCalculatedCart(
      state,
      action: PayloadAction<{
        subtotalPrice: number;
        deliveryPrice: number;
        totalPrice: number;
        appliedPromotions: CalculateCartPromotion[];
        earnedAmount: number;
        availableAmountForCurrentOrder: number;
        availablePoints: number;
        bonusPaymentPercent: number | null;
        promoCode: string;
        isPromoValid: boolean | null;
        couponStatus: string | null;
      }>,
    ) {
      state.isCalculatingCart = false;
      state.calculatedSubtotalPrice = action.payload.subtotalPrice;
      state.calculatedDeliveryPrice = action.payload.deliveryPrice;
      state.calculatedTotalPrice = action.payload.totalPrice;
      state.appliedPromotions = action.payload.isPromoValid === false
        ? []
        : action.payload.appliedPromotions;
      state.earnedAmount = action.payload.earnedAmount;
      state.availableAmountForCurrentOrder =
        action.payload.availableAmountForCurrentOrder;
      state.availablePoints = action.payload.availablePoints;
      state.bonusPaymentPercent = action.payload.bonusPaymentPercent;
      // Промокод в расчёте — баллы вместе с ним не списываются.
      if (state.promoCode) state.pointsEnabled = false;
      // Включённый тумблер всегда списывает актуальную сумму Mindbox: она
      // меняется вместе с корзиной, и списание должно идти за ней.
      state.selectedBonusPay = state.pointsEnabled
        ? action.payload.availableAmountForCurrentOrder
        : 0;
      // Статус кода — только из ответа на текущий код: ответ на прежний
      // (запрос ушёл до смены или отмены) статус не трогает.
      if (action.payload.promoCode !== state.promoCode) return;

      state.activePromoCode = action.payload.isPromoValid
        ? action.payload.promoCode
        : "";
      state.couponStatus = action.payload.couponStatus;
      // Отказ код не снимает — см. promoCode: он проверится заново на
      // следующем составе корзины.
      state.promoValidationStatus = action.payload.isPromoValid
        ? "valid"
        : action.payload.isPromoValid === false
          ? "invalid"
          : "none";
    },
    /** Ручная отмена промокода и выход из аккаунта. */
    resetPromo(state) {
      resetPromoState(state);
    },
    /**
     * «Применить» — на экране «Промокоды» или на чекауте. Новый код заменяет
     * прежний. Промокод и баллы вместе не применяются, поэтому списание
     * выключаем (на чекауте «Применить» при включённых баллах и так закрыто).
     */
    applyPromoCode(state, action: PayloadAction<string>) {
      const code = action.payload.trim();
      if (code !== state.promoCode) resetPromoState(state);
      state.promoCode = code;
      state.pointsEnabled = false;
      state.selectedBonusPay = 0;
    },
    setPointsEnabled(state, action: PayloadAction<boolean>) {
      // Промокод и баллы вместе не применяются: пока код применён или ждёт
      // проверки, включить списание нельзя.
      if (action.payload && state.promoCode) return;
      state.pointsEnabled = action.payload;
      state.selectedBonusPay = action.payload
        ? state.availableAmountForCurrentOrder
        : 0;
    },
    setPromoInvalid(
      state,
      action: PayloadAction<{ couponStatus?: string | null } | undefined>,
    ) {
      state.isCalculatingCart = false;
      state.calculatedSubtotalPrice = null;
      state.calculatedDeliveryPrice = null;
      state.calculatedTotalPrice = null;
      state.appliedPromotions = [];
      state.promoValidationStatus = "invalid";
      state.couponStatus = action.payload?.couponStatus ?? null;
      // Сам код не снимаем — см. promoCode.
      state.activePromoCode = "";
    },
    resetCalculatedCart(state) {
      state.isCalculatingCart = false;
      state.calculatedSubtotalPrice = 0;
      state.calculatedDeliveryPrice = 0;
      state.calculatedTotalPrice = 0;
      state.appliedPromotions = [];
      state.earnedAmount = 0;
      state.availableAmountForCurrentOrder = 0;
      state.availablePoints = 0;
      state.pointsEnabled = false;
      state.selectedBonusPay = 0;
      state.promoValidationStatus = "none";
      state.couponStatus = null;
      state.activePromoCode = "";
    },
    resetCalculatedPrices(state) {
      state.isCalculatingCart = true;
      // Keep previous calculated prices visible while recalculating to avoid UI jumps
      // state.calculatedSubtotalPrice = null;
      // state.calculatedDeliveryPrice = null;
      // state.calculatedTotalPrice = null;
      state.appliedPromotions = state.appliedPromotions; // keep existing promotions
    },
  },
});

export const {
  addToBasket,
  removeFromBasket,
  increaseQuantity,
  decreaseQuantity,
  clearBasket,
  setBasketFromServer,
  hydrateBasketFromStorage,
  setAvailabilityPending,
  removeAllNotAvailableItems,
  removeNotAvailableItems,
  updateBasketWithNewMenu,
  setCartCalculating,
  setCalculatedCart,
  resetPromo,
  applyPromoCode,
  setPointsEnabled,
  setPromoInvalid,
  resetCalculatedCart,
  resetCalculatedPrices,
} = basketSlice.actions;

export default basketSlice.reducer;
